import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { useAtom } from 'jotai';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { goalsAnalyticsAtom } from '../atoms/sessionGoalsAtoms';

/**
 * Hook for tracking analytics events in the SessionGoals component.
 *
 * Provides centralized tracking with common properties (loadId, locationContext, appointmentId)
 * automatically included in all events.
 *
 * @param patientId - The patient ID (required for context)
 * @param appointmentId - Optional appointment ID to include in tracking
 */
export const useGoalsAnalytics = (
  patientId: string,
  appointmentId?: string,
) => {
  const { trackEvent } = useAnalytics();
  const [analytics, setAnalytics] = useAtom(goalsAnalyticsAtom);
  const pathname = usePathname();

  // Derive locationContext from URL
  // - "/appointments/*" paths are post-call (charting note review)
  // - All other paths are in-session (during video call)
  const locationContext = useMemo(() => {
    return pathname?.includes('/appointments/') ? 'post-call' : 'in-session';
  }, [pathname]);

  // Generate loadId on mount (if not already set)
  useEffect(() => {
    if (!analytics.loadId) {
      setAnalytics((prev) => ({ ...prev, loadId: uuidv4() }));
    }
  }, [analytics.loadId, setAnalytics]);

  // Common properties for all events
  const getCommonProperties = useCallback(
    () => ({
      loadId: analytics.loadId,
      locationContext,
      appointmentId,
    }),
    [analytics.loadId, locationContext, appointmentId],
  );

  /**
   * Track when the goals widget becomes visible in the viewport.
   * Only fires once per widget load (hasTrackedView guard).
   */
  const trackWidgetViewed = useCallback(() => {
    if (analytics.hasTrackedView || !analytics.loadId) return;
    trackEvent(EVENT_NAMES.GOALS_WIDGET_VIEWED, getCommonProperties());
    setAnalytics((prev) => ({ ...prev, hasTrackedView: true }));
  }, [
    analytics.hasTrackedView,
    analytics.loadId,
    trackEvent,
    getCommonProperties,
    setAnalytics,
  ]);

  /**
   * Track when a new goal is created.
   * @param goalId - The ID of the newly created goal
   */
  const trackGoalAdded = useCallback(
    (goalId: string) => {
      trackEvent(EVENT_NAMES.GOAL_ADDED, { ...getCommonProperties(), goalId });
    },
    [trackEvent, getCommonProperties],
  );

  /**
   * Track when a goal is archived.
   * @param goalId - The ID of the archived goal
   */
  const trackGoalArchived = useCallback(
    (goalId: string) => {
      trackEvent(EVENT_NAMES.GOAL_ARCHIVED, {
        ...getCommonProperties(),
        goalId,
      });
    },
    [trackEvent, getCommonProperties],
  );

  /**
   * Track when a goal's content is edited.
   * @param goalId - The ID of the edited goal
   */
  const trackGoalEdited = useCallback(
    (goalId: string) => {
      trackEvent(EVENT_NAMES.GOAL_EDITED, { ...getCommonProperties(), goalId });
    },
    [trackEvent, getCommonProperties],
  );

  return {
    trackWidgetViewed,
    trackGoalAdded,
    trackGoalArchived,
    trackGoalEdited,
  };
};
